import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface DocumentTemplate {
  id: string;
  type: 'nda' | 'contract' | 'quote' | 'cgv' | 'access_form' | 'presentation';
  name: string;
  version: string;
  content: {
    title: string;
    subtitle?: string;
    preambule?: string;
    articles: Array<{
      number: number;
      title: string;
      content: string;
    }>;
    footer?: string;
  };
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

// ============================================================================
// Hook: useDocumentTemplate - Récupère un template par type
// ============================================================================
export const useDocumentTemplate = (type: string) => {
  return useQuery({
    queryKey: ['document-template', type],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_templates')
        .select('*')
        .eq('type', type)
        .eq('is_active', true)
        .eq('is_default', true)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as DocumentTemplate | null;
    },
    enabled: !!type,
  });
};

// ============================================================================
// Hook: useDocumentTemplates - Récupère tous les templates
// ============================================================================
export const useDocumentTemplates = () => {
  return useQuery({
    queryKey: ['document-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_templates')
        .select('*')
        .order('type', { ascending: true });

      if (error) throw error;
      return data as DocumentTemplate[];
    },
  });
};

// ============================================================================
// Hook: useUpdateDocumentTemplate - Met à jour un template
// ============================================================================
export const useUpdateDocumentTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<DocumentTemplate>;
    }) => {
      const { data, error } = await supabase
        .from('document_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as DocumentTemplate;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
      queryClient.invalidateQueries({ queryKey: ['document-template', data.type] });
    },
  });
};

// ============================================================================
// Hook: useCreateDocumentTemplate - Crée un nouveau template
// ============================================================================
export const useCreateDocumentTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: Partial<DocumentTemplate>) => {
      const { data, error } = await supabase
        .from('document_templates')
        .insert(template)
        .select()
        .single();

      if (error) throw error;
      return data as DocumentTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    },
  });
};

